import { useEffect, useRef, useState, useCallback } from 'react';
import Phaser from 'phaser';
import GameScene from './scenes/GameScene';
import UIScene from './scenes/UIScene';
import SplashScene from './scenes/SplashScene';
import HomeScene from './scenes/HomeScene';
import LeaderboardScene from './scenes/LeaderboardScene';
import { initIAP } from './IAPManager';

function OfflineScreen({ onRetry }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #f8f0ff 0%, #ebe8ff 50%, #f0e8ff 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Georgia, "Times New Roman", serif', color: '#7722aa', textAlign: 'center',
      padding: '24px',
    }}>
      <div style={{ fontSize: '64px', marginBottom: '16px' }}>📡</div>
      <h2 style={{ fontSize: '24px', fontStyle: 'italic', margin: '0 0 12px 0', color: '#9933cc' }}>
        No Internet Connection
      </h2>
      <p style={{ fontSize: '15px', fontStyle: 'italic', color: '#5a2a7a', maxWidth: '280px', lineHeight: 1.5, margin: '0 0 28px 0' }}>
        Food Stack requires an internet connection to play. Please check your connection and try again.
      </p>
      <button
        onClick={onRetry}
        style={{
          background: '#9944cc', color: '#fff', border: 'none', borderRadius: '28px',
          padding: '14px 48px', fontSize: '17px', fontFamily: 'Georgia, "Times New Roman", serif',
          fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 14px rgba(153,68,204,0.3)',
        }}
      >
        Retry
      </button>
    </div>
  );
}

export default function PhaserGame() {
  const gameRef = useRef(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const handleRetry = useCallback(() => {
    if (navigator.onLine) {
      setIsOffline(false);
    }
  }, []);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline  = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online',  goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online',  goOnline);
    };
  }, []);

  useEffect(() => {
    if (isOffline) {
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }
      return;
    }

    if (gameRef.current) return;

    initIAP();

    import('@dimforge/rapier2d-compat').then(async (RAPIER) => {
      await RAPIER.init();
      window.__RAPIER__ = RAPIER;

      const dpr = window.devicePixelRatio || 1;
      const maxAspect = 9 / 16;
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;
      const isLandscape = screenW / screenH > maxAspect;
      const gameW = isLandscape ? Math.floor(screenH * maxAspect) : screenW;

      const container = document.getElementById('phaser-container');
      if (isLandscape && container) {
        container.style.width = gameW + 'px';
        container.style.maxWidth = gameW + 'px';
      }

      const config = {
        type: Phaser.AUTO,
        parent: 'phaser-container',
        width: gameW * dpr,
        height: screenH * dpr,
        resolution: dpr,
        backgroundColor: '#a8d8f0',
        scene: [SplashScene, HomeScene, LeaderboardScene, GameScene, UIScene],
        scale: {
          mode: Phaser.Scale.FIT,
          autoCenter: Phaser.Scale.CENTER_BOTH,
          width: gameW * dpr,
          height: screenH * dpr,
        },
      };

      gameRef.current = new Phaser.Game(config);
    });

    return () => {
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }
    };
  }, [isOffline]);

  if (isOffline) {
    return <OfflineScreen onRetry={handleRetry} />;
  }

  return (
    <div
      id="phaser-container"
      style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}
    />
  );
}
